/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment } from 'react'

import { EuiPanel, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { CuiTableColumn } from '@modules/cui/Table'
import { CuiTable } from '@modules/cui/Table'
import { DocLink } from '@modules/doclinks'

import EmptyDataPrompt from '@/components/Billing/BillingUsage/BillingUsageOverviewV2/EmptyDataPrompt'
import DetailsPanel from '@/components/Billing/BillingUsage/BillingUsageOverviewV2/DetailsPanel'

import ResponsivePanelWrapper from '../components/ResponsivePanelWrapper'
import useFilters from '../components/Filters/useFilters'

import { IconType } from './types'

import type { ReactNode } from 'react'

export interface BillingUsageTableProps<T extends { id: string }> {
  title: ReactNode
  rows: T[]
  columns: Array<CuiTableColumn<T>>
  isLoading: boolean
  organizationId: string
  renderCustomRowsLast: () => ReactNode
  dataTestId: string
}

const BillingUsageTable = <T extends { id: string }>({
  title,
  rows,
  columns,
  isLoading,
  organizationId,
  renderCustomRowsLast,
  dataTestId,
}: BillingUsageTableProps<T>) => {
  const { filters } = useFilters()

  const hasAnyItems = Boolean(rows.length)
  const getRowId = (item: T) => item.id
  const hasDetailRow = (item: T) => !!getRowId(item)
  const emptyMessage = <EmptyDataPrompt iconType={IconType.TABLE} />

  const description = (
    <FormattedMessage
      id='billing-deployment-usage-list.description'
      defaultMessage='All cost units are measured in {ecu}.'
      values={{
        ecu: (
          <DocLink link='billingECU'>
            <FormattedMessage
              id='ecu-tooltip.page-ecu-unit'
              defaultMessage='Elastic Consumption Units (ECU)'
            />
          </DocLink>
        ),
      }}
    />
  )

  const renderDetailRow = (item: T) => (
    <DetailsPanel
      instanceId={getRowId(item)}
      organizationId={organizationId}
      startDate={filters.startDate}
      endDate={filters.endDate}
    />
  )

  return (
    <Fragment>
      <EuiText size='s'>
        <h3>{title}</h3>
      </EuiText>

      <EuiSpacer size='xs' />

      <EuiText size='s'>{description}</EuiText>

      <EuiSpacer size='m' />

      <EuiPanel hasShadow={false} paddingSize='m' hasBorder={true} data-test-id={dataTestId}>
        <ResponsivePanelWrapper>
          <CuiTable
            rows={rows}
            columns={columns}
            initialLoading={isLoading}
            hasFooterRow={hasAnyItems}
            responsiveBreakpoint={false}
            getRowId={getRowId}
            renderDetailRow={renderDetailRow}
            hasDetailRow={hasDetailRow}
            renderDetailButton={true}
            emptyMessage={emptyMessage}
            renderCustomRowsLast={hasAnyItems ? renderCustomRowsLast : undefined}
          />
        </ResponsivePanelWrapper>
      </EuiPanel>
    </Fragment>
  )
}

export default BillingUsageTable
